<template>
  <div class="page boss-page project-value-ranking-page">
    <van-nav-bar title="项目排序" left-arrow @click-left="$router.back()"/>

    <section class="intro">
      <div class="intro-title">
        <van-icon name="bar-chart-o"/>
        <b>按价值看项目</b>
      </div>
      <p>拖动右侧排序柄即可调整查看顺序，松手后自动保存；不会改变项目业务数据。</p>
    </section>

    <div class="toolbar">
      <div class="toolbar-main">
        <span>排序依据</span>
        <select v-model="sortKey" aria-label="排序依据" @change="onSortChanged">
          <option value="value">累计价值</option>
          <option value="roi">投效比</option>
          <option value="risk">风险优先</option>
          <option value="progress">成果进度</option>
          <option value="manual">手动排序（可拖动）</option>
        </select>
      </div>
      <small class="toolbar-hint">{{ sortHint }}</small>
      <small class="order-status" :class="{saving: savingOrder, failed: orderStatus.indexOf('失败') >= 0}">
        {{ orderStatus }}
      </small>
    </div>

    <van-loading v-if="loading" class="loading" color="#c62828">加载中</van-loading>
    <section v-else class="list">
      <div
        v-for="(project,index) in sortedProjects"
        :key="project.id"
        :data-index="index"
        class="row project-value-ranking-row"
        :class="{dragging: dragIndex === index, 'drop-target': dragIndex !== null && dragIndex !== index && dragOverIndex === index}"
        :draggable="canDrag"
        role="listitem"
        @dragstart="onDragStart(index, $event)"
        @dragover.prevent="onDragOver(index)"
        @dragenter.prevent="dragOverIndex = index"
        @drop.prevent="onDrop(index)"
        @dragend="onDragEnd"
      >
        <b class="rank">{{ index + 1 }}</b>
        <div class="copy" @click="openDetail(project)">
          <strong>{{ project.name }}</strong>
          <small>{{ project.projectCode || '未设置编号' }} · {{ health(project.healthLevel) }}</small>
          <small v-if="project.value" class="row-value">价值：{{ project.value }}</small>
        </div>
        <div class="value">
          <strong>{{ metric(project) }}</strong>
          <small>{{ metricLabel }}</small>
        </div>
        <button
          type="button"
          class="drag-handle"
          :class="{disabled: !canDrag}"
          aria-label="拖动调整项目顺序"
          @touchstart.stop="onTouchStart(index, $event)"
          @touchmove.stop.prevent="onTouchMove($event)"
          @touchend.stop="onTouchEnd"
          @touchcancel.stop="onTouchEnd"
        >
          <van-icon name="bars"/>
        </button>
      </div>

      <van-empty v-if="!sortedProjects.length" :description="isBoss ? '暂无项目投效数据' : '暂无您参与的项目'">
        <van-button v-if="isBoss" size="small" type="danger" @click="$router.push({name:'BossProjectPortfolio',query:{create:'1'}})">
          创建项目
        </van-button>
      </van-empty>
    </section>

    <van-popup
      v-model="detailVisible"
      position="bottom"
      round
      :style="{ height: '82%' }"
      class="project-detail-popup"
    >
      <div class="detail-header">
        <b class="detail-title">{{ detail && detail.project ? detail.project.name : '项目详情' }}</b>
        <van-icon name="cross" class="detail-close" @click="detailVisible = false" />
      </div>
      <van-loading v-if="detailLoading" class="loading" color="#c62828">加载中</van-loading>
      <div v-else-if="detail && detail.project" class="detail-body">
        <section class="detail-block">
          <h4>项目介绍</h4>
          <p>{{ detail.project.description || '暂无' }}</p>
        </section>
        <section class="detail-block">
          <h4>项目目标</h4>
          <p>{{ detail.project.goal || '暂无' }}</p>
        </section>
        <section class="detail-block">
          <h4>项目价值</h4>
          <p>{{ detail.project.value || '暂无' }}</p>
        </section>
        <section class="detail-block">
          <h4>项目关联人员</h4>
          <div v-if="detailMembersGrouped.length">
            <div v-for="g in detailMembersGrouped" :key="g.depName" class="member-group">
              <span class="member-dep">{{ g.depName }}</span>
              <span class="member-names">{{ g.names.join('、') }}</span>
            </div>
          </div>
          <p v-else class="empty-members">暂无关联人员</p>
        </section>
      </div>
    </van-popup>
  </div>
</template>

<script>
import coreapi from '@/utils/coreapi';

export default {
  name: 'BossProjectValueRanking',
  data() {
    return {
      loading: false,
      projects: [],
      sortKey: 'value',
      manualOrder: [],
      lastSavedOrder: [],
      dragStartOrder: [],
      dragIndex: null,
      dragOverIndex: null,
      touchIndex: null,
      dragChanged: false,
      savingOrder: false,
      orderStatus: '可拖动排序',
      detailVisible: false,
      detailLoading: false,
      detail: null
    };
  },
  computed: {
    isBoss() {
      return localStorage.getItem('isBoss') === '1' || localStorage.getItem('CoreIsBoss') === 'true';
    },
    canDrag() {
      return this.isBoss && !this.savingOrder && this.sortedProjects.length > 0;
    },
    sortedProjects() {
      const list = this.projects.slice();
      if (this.sortKey === 'manual') {
        const byId = {};
        list.forEach(project => { byId[String(project.id)] = project; });
        const ordered = [];
        const seen = {};
        this.manualOrder.forEach(id => {
          const key = String(id);
          if (byId[key] && !seen[key]) {
            ordered.push(byId[key]);
            seen[key] = true;
          }
        });
        list.forEach(project => {
          const key = String(project.id);
          if (!seen[key]) {
            ordered.push(project);
            seen[key] = true;
          }
        });
        return ordered;
      }

      return list.sort((a, b) => {
        const difference = this.number(b, this.sortKey) - this.number(a, this.sortKey);
        if (difference !== 0) return difference;
        return Number(a.id || 0) - Number(b.id || 0);
      });
    },
    metricLabel() {
      return {
        value: '累计价值',
        manual: '累计价值',
        roi: '投效比',
        risk: '风险等级',
        progress: '成果进度'
      }[this.sortKey] || '累计价值';
    },
    sortHint() {
      return this.sortKey === 'manual'
        ? '当前为人工顺序，按住右侧排序柄上下拖动即可调整。'
        : '当前为指标排序；直接拖动时会保留当前顺序并切换为人工排序。';
    },
    detailMembersGrouped() {
      if (!this.detail || !this.detail.functionMembers) return [];
      const map = {};
      this.detail.functionMembers.forEach(function (m) {
        const dep = m.depName || '未分组';
        (map[dep] = map[dep] || []).push(m.memberName || m.memberId || '');
      });
      return Object.keys(map).map(function (dep) {
        return { depName: dep, names: map[dep] };
      });
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const data = await coreapi.bossProjectPortfolio();
        const list = data && Array.isArray(data.projects) ? data.projects : [];
        this.projects = list.map(project => Object.assign({}, project));
        this.manualOrder = this.projects.slice().sort(this.compareOrder).map(project => project.id);
        this.lastSavedOrder = this.manualOrder.slice();
        this.orderStatus = this.manualOrder.length ? '可拖动排序' : '暂无可排序项目';
      } catch (e) {
        const msg = (e && e.message) || '';
        if (/boss permission|permission required|无权限|权限/i.test(msg)) {
          this.projects = [];
          this.manualOrder = [];
          this.lastSavedOrder = [];
          this.orderStatus = '暂无项目排序权限';
        } else {
          this.$toast.fail(msg || '项目数据加载失败');
        }
      } finally {
        this.loading = false;
      }
    },
    compareOrder(a, b) {
      const difference = this.orderValue(a) - this.orderValue(b);
      if (difference !== 0) return difference;
      return Number(a.id || 0) - Number(b.id || 0);
    },
    orderValue(project) {
      const value = Number(project && project.sortOrder);
      return Number.isFinite(value) && value >= 0 ? value : Number.MAX_SAFE_INTEGER;
    },
    onSortChanged() {
      this.dragIndex = null;
      this.dragOverIndex = null;
      this.dragChanged = false;
      this.orderStatus = this.sortKey === 'manual' ? '可拖动排序' : '指标排序（仅查看）';
    },
    prepareDrag(index) {
      if (!this.isBoss || this.savingOrder || !this.sortedProjects.length) return false;
      if (this.sortKey !== 'manual') {
        this.manualOrder = this.sortedProjects.map(project => project.id);
        this.sortKey = 'manual';
      }
      this.dragStartOrder = this.manualOrder.slice();
      this.dragIndex = index;
      this.dragOverIndex = index;
      this.dragChanged = false;
      this.orderStatus = '拖动中…';
      return true;
    },
    onDragStart(index, event) {
      if (!this.prepareDrag(index)) {
        if (event && event.preventDefault) event.preventDefault();
        return;
      }
      if (event && event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(this.sortedProjects[index].id));
      }
    },
    onDragOver(index) {
      if (this.dragIndex === null) return;
      this.dragOverIndex = index;
      this.moveItem(this.dragIndex, index);
    },
    onDrop(index) {
      if (this.dragIndex === null) return;
      this.dragOverIndex = index;
      this.moveItem(this.dragIndex, index);
    },
    moveItem(from, to) {
      const list = this.sortedProjects.map(project => project.id);
      if (from === null || to === null || from < 0 || to < 0 || from >= list.length || to >= list.length || from === to) {
        return false;
      }
      const moved = list.splice(from, 1)[0];
      list.splice(to, 0, moved);
      this.manualOrder = list;
      this.dragIndex = to;
      this.dragChanged = true;
      return true;
    },
    onDragEnd() {
      this.finishDrag();
    },
    onTouchStart(index, event) {
      if (!event || !event.touches || event.touches.length !== 1) return;
      if (this.prepareDrag(index)) this.touchIndex = index;
    },
    onTouchMove(event) {
      if (this.touchIndex === null || !event || !event.touches || !event.touches.length) return;
      const touch = event.touches[0];
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      let row = target;
      while (row && row !== document.body && (!row.getAttribute || row.getAttribute('data-index') === null)) {
        row = row.parentNode;
      }
      if (!row || row === document.body) return;
      const nextIndex = Number(row.getAttribute('data-index'));
      if (Number.isInteger(nextIndex) && nextIndex !== this.dragIndex) {
        this.dragOverIndex = nextIndex;
        this.moveItem(this.dragIndex, nextIndex);
      }
    },
    onTouchEnd() {
      this.touchIndex = null;
      this.finishDrag();
    },
    finishDrag() {
      if (this.dragIndex === null) return;
      const changed = this.dragChanged;
      const rollbackOrder = this.dragStartOrder.slice();
      this.dragIndex = null;
      this.dragOverIndex = null;
      this.dragChanged = false;
      this.dragStartOrder = [];
      if (changed) {
        this.persistManualOrder(rollbackOrder);
      } else {
        this.orderStatus = '可拖动排序';
      }
    },
    async persistManualOrder(rollbackOrder) {
      if (!this.isBoss) return;
      const projectIds = this.sortedProjects.map(project => project.id);
      if (!projectIds.length) return;
      this.savingOrder = true;
      this.orderStatus = '正在保存…';
      try {
        const result = await coreapi.saveBossProjectOrder(projectIds);
        const savedOrder = result && Array.isArray(result.projectIds) ? result.projectIds : projectIds;
        this.manualOrder = savedOrder.slice();
        this.lastSavedOrder = savedOrder.slice();
        this.orderStatus = '已保存';
      } catch (e) {
        if (rollbackOrder.length) this.manualOrder = rollbackOrder.slice();
        this.orderStatus = '保存失败，可重试';
        this.$toast.fail(e.message || '排序保存失败');
      } finally {
        this.savingOrder = false;
      }
    },
    number(project, key) {
      if (key === 'value' || key === 'manual') {
        return Number(project.totalValue || project.realizedValue || project.valueAmount || 0);
      }
      if (key === 'roi') return Number(project.roiPercent || 0);
      if (key === 'progress') return Number(project.outcomeCompletionRate || project.progressPercent || 0);
      return ({RISK: 3, WATCH: 2, HEALTHY: 1}[project.healthLevel] || 0);
    },
    metric(project) {
      if (this.sortKey === 'roi') return this.number(project, 'roi').toFixed(1) + '%';
      if (this.sortKey === 'progress') return this.number(project, 'progress').toFixed(0) + '%';
      if (this.sortKey === 'risk') return this.health(project.healthLevel);
      return '¥' + this.number(project, 'value').toLocaleString();
    },
    health(value) {
      return {HEALTHY: '健康', WATCH: '关注', RISK: '风险'}[value] || '待计算';
    },
    async openDetail(project) {
      if (!project || project.id == null) return;
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const data = await coreapi.bossProjectDetail(project.id);
        this.detail = data || { project: {} };
      } catch (e) {
        this.$toast.fail((e && e.message) || '项目详情加载失败');
        this.detail = { project: {} };
      } finally {
        this.detailLoading = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
@import './boss.less';

.page {
  min-height: 100vh;
  margin: 0;
  padding-bottom: 1.5rem;
  background: #f6f7fb;
  color: #30313a;
}

.page /deep/ .van-nav-bar {
  margin: 0;
  width: 100%;
  height: 1.22667rem;
  border-radius: 0;
  background: linear-gradient(135deg, #351b1d, #c6282d);
}

.intro {
  margin: .16rem .24rem;
  padding: .2rem .22rem;
  color: #303344;
  background: #eef0f5;
  border-radius: .12rem;
}

.intro-title {
  display: flex;
  align-items: center;
  gap: .1rem;
}

.intro-title .van-icon {
  color: #c6282d;
  font-size: .34rem;
}

.intro b {
  font-size: .3rem;
}

.intro p {
  margin: .06rem 0 0;
  color: #73778a;
  font-size: .22rem;
  line-height: 1.45;
}

.toolbar {
  margin: .12rem .24rem;
  padding: .16rem .2rem .14rem;
  background: #fff;
  border-radius: .14rem;
  box-shadow: 0 .04rem .16rem rgba(30, 35, 60, .06);
}

.toolbar-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toolbar-main span {
  color: #73778a;
  font-size: .24rem;
}

.toolbar select {
  min-width: 2.25rem;
  height: .58rem;
  padding: 0 .14rem;
  color: #30313a;
  border: 1PX solid #ddd;
  border-radius: .08rem;
  outline: 0;
  background: #fff;
  font-size: .23rem;
}

.toolbar-hint,
.order-status {
  display: block;
  margin-top: .1rem;
  color: #9295a7;
  font-size: .2rem;
  line-height: 1.4;
}

.order-status {
  margin-top: .04rem;
  color: #2e9b68;
}

.order-status.saving {
  color: #c27a1a;
}

.order-status.failed {
  color: #c6282d;
}

.loading {
  display: block;
  margin: 1rem auto;
}

.list {
  margin: 0 .24rem;
  overflow: hidden;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .06rem .24rem rgba(30, 35, 60, .06);
}

.row {
  min-height: 1.08rem;
  padding: .18rem .16rem .18rem .2rem;
  display: flex;
  align-items: center;
  border-bottom: 1PX solid #eee;
  transition: background-color .15s, opacity .15s, box-shadow .15s;
  user-select: none;
}

.row:last-child {
  border-bottom: 0;
}

.row.dragging {
  opacity: .55;
  background: #fff5f5;
}

.row.drop-target {
  box-shadow: inset 0 -2PX 0 #c6282d;
}

.rank {
  width: .4rem;
  flex: 0 0 .4rem;
  color: #c6282d;
  text-align: center;
  font-size: .28rem;
}

.copy {
  min-width: 0;
  flex: 1;
  margin-left: .12rem;
}

.copy strong,
.copy small,
.value strong,
.value small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy strong {
  color: #30313a;
  font-size: .28rem;
}

.copy small {
  margin-top: .04rem;
  color: #9295a7;
  font-size: .21rem;
}

.copy .row-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #8a6d3b;
  font-size: .21rem;
}

.value {
  flex: 0 0 auto;
  margin-right: .08rem;
  text-align: right;
}

.value strong {
  color: #c6282d;
  font-size: .27rem;
}

.value small {
  margin-top: .04rem;
  color: #9295a7;
  font-size: .19rem;
}

.drag-handle {
  width: .6rem;
  min-width: .6rem;
  height: .68rem;
  min-height: .68rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #c6282d;
  border: 0;
  border-radius: .1rem;
  outline: 0;
  background: transparent;
  cursor: grab;
  touch-action: none;
}

.drag-handle:active {
  background: #fff0f0;
  cursor: grabbing;
}

.drag-handle.disabled {
  opacity: .45;
  cursor: wait;
}

.page /deep/ .van-empty {
  padding: .48rem 0 .56rem;
}

.page /deep/ .van-empty__image {
  width: 1.55rem;
  height: 1.2rem;
}

.page /deep/ .van-empty__description {
  margin-top: .12rem;
  color: #85899c;
  font-size: .24rem;
}

.boss-page > .van-nav-bar.van-nav-bar--fixed,
.boss-page > .van-nav-bar {
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
  width: 100vw !important;
  border-radius: 0 !important;
}

.project-detail-popup {
  display: flex;
  flex-direction: column;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: .24rem .28rem;
  border-bottom: 1PX solid #eee;
}

.detail-title {
  color: #30313a;
  font-size: .32rem;
}

.detail-close {
  font-size: .42rem;
  color: #9295a7;
}

.detail-body {
  flex: 1;
  overflow-y: auto;
  padding: .24rem .28rem .4rem;
}

.detail-block {
  margin-bottom: .28rem;
}

.detail-block h4 {
  margin: 0 0 .1rem;
  color: #c6282d;
  font-size: .26rem;
}

.detail-block p {
  margin: 0;
  color: #303344;
  font-size: .26rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.member-group {
  display: flex;
  align-items: baseline;
  margin-bottom: .12rem;
  font-size: .26rem;
}

.member-dep {
  flex: 0 0 auto;
  margin-right: .16rem;
  padding: .02rem .14rem;
  color: #2f6bd0;
  background: #eef3fc;
  border-radius: .08rem;
}

.member-names {
  color: #303344;
  line-height: 1.6;
}

.empty-members {
  margin: 0;
  color: #9295a7;
  font-size: .26rem;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/ProjectValueRanking.vue